import { memo, useCallback } from 'react';
import c from 'classnames';
import times from 'lodash/times';

import useScreen from '@lyearn/core/hooks/useScreen';
import { ArrowIosLeftFill, ArrowIosRightFill } from '@lyearn/icons';
import { cn } from '@lyearn/style';
import { Box, Stack } from '@lyearn/ui';

import {
  arrowColorStyles,
  bulletColorStyles,
  carouselPaginationStyles,
  paginationInSlide,
} from './styles';
import { CarouselPaginationProps } from './types';

const CarouselPagination = (props: CarouselPaginationProps) => {
  const {
    activePage,
    className = 'mt-16',
    renderPaginationArrows,
    getPaginationColor,
    pages,
    onChange,
  } = props;

  const onLeftArrowClick = useCallback(() => {
    onChange(activePage - 1);
  }, [activePage, onChange]);

  const onRightArrowClick = useCallback(() => {
    onChange(activePage + 1);
  }, [activePage, onChange]);

  const { isMobile } = useScreen();

  if (pages <= 1) {
    return null;
  }

  const iconColors = getPaginationColor?.(activePage);
  const activeBulletColor = iconColors?.activeBulletColor;
  const showLeftArrow = activePage > 0;
  const showRightArrow = activePage < pages - 1;

  return (
    <Stack
      alignItems="center"
      className={cn('p-24 z-10', isMobile ? 'pb-12' : '', {
        [paginationInSlide]: renderPaginationArrows,
      })}
      justifyContent={renderPaginationArrows ? 'between' : 'center'}>
      {renderPaginationArrows ? (
        showLeftArrow ? (
          <ArrowIosLeftFill
            className={cn('rtl:rotate-180 p-8 cursor-pointer', arrowColorStyles(activeBulletColor))}
            height={40}
            width={40}
            onClick={onLeftArrowClick}
          />
        ) : (
          <Box className="h-[4rem] w-[4rem]" />
        )
      ) : null}
      <Stack
        className={c(carouselPaginationStyles, className, {
          [bulletColorStyles(activeBulletColor)]: renderPaginationArrows,
        })}
        gapX={8}
        justifyContent="center">
        {times(pages, (index) => (
          <Stack
            key={index}
            className={c('pagination-bullet cursor-pointer rounded-full', {
              'pagination-bullet-active': index === activePage,
            })}
            onClick={() => onChange(index)}
          />
        ))}
      </Stack>
      {renderPaginationArrows ? (
        showRightArrow ? (
          <ArrowIosRightFill
            className={cn('rtl:rotate-180 p-8 cursor-pointer', arrowColorStyles(activeBulletColor))}
            height={40}
            width={40}
            onClick={onRightArrowClick}
          />
        ) : (
          <Box className="h-[4rem] w-[4rem]" />
        )
      ) : null}
    </Stack>
  );
};

export default memo(CarouselPagination);
